import {
  ShapeKind, ShapeKindGroupFigure, DrawingShape, Fillable, Tiling, Point, Offset, FontType, Line,
  StraightLineShape, PolylineShape, PolygonShape, LineType, LineCaps, NoteShape, MultiroleLinkShape,
} from '../types';
import { MultiroleLinkInfo } from '../../../../../common/src/ts/types/multiroleLink';
import { Action } from 'redux';

export enum ActionNames {
  ACTIVATE_LAYER = 'DRAWING_ACTIVATE_LAYER',
  REPLACE_HANDWRITING_TILES = 'DRAWING_REPLACE_HANDWRITING_TILES',
  ADD_FIGURE = 'DRAWING_ADD_FIGURE',
  ADD_STICKY = 'DRAWING_ADD_STICKY',
  ADD_BALLOON = 'DRAWING_ADD_BALLOON',
  ADD_POLYLINE = 'DRAWING_ADD_POLYLINE',
  ADD_POLYGON = 'DRAWING_ADD_POLYGON',
  ADD_STRAIGHT_LINE = 'DRAWING_ADD_STRAIGHT_LINE',
  ADD_STAMP = 'DRAWING_ADD_STAMP',
  PASTE_IMAGE = 'DRAWING_PASTE_IMAGE',
  PASTE_MULTIROLE_LINK = 'DRAWING_PASTE_MULTIROLE_LINK',
  MOVE_LAYER = 'DRAWING_MOVE_LAYER',
  RESIZE_LAYER = 'DRAWING_RESIZE_LAYER',
  REMOVE_LAYER = 'DRAWING_REMOVE_LAYER',
  REMOVE_MULTIROLE_LINK = 'DRAWING_REMOVE_MULTIROLE_LINK',
  CLEAR_ALL_OBJECTS = 'DRAWING_CLEAR_ALL_OBJECTS',
  CLEAR_ALL_LEARNING_PEN_RECTS = 'DRAWING_CLEAR_ALL_LEARNING_PEN_RECTS',
  CLEAR_ALL_HANDWRITING = 'DRAWING_CLEAR_ALL_HANDWRITING',
  CLEAR_EVERYTHING = 'DRAWING_CLEAR_EVERYTHING',
  START_TEXT_EDITING = 'DRAWING_START_TEXT_EDITING',
  CHANGE_TEXT = 'DRAWING_CHANGE_TEXT',
  MOVE_POINT = 'DRAWING_MOVE_POINT',
  FOLD_NOTE = 'DRAWING_FOLD_NOTE',
  TOGGLE_TRANSPARENCY_OF_LEARNING_PEN = 'DRAWING_TOGGLE_TRANSPARENCY_OF_LEARNING_PEN',
  TOGGLE_TRANSPARENCY_OF_ALL_LEARNING_PENS = 'DRAWING_TOGGLE_TRANSPARENCY_OF_ALL_LEARNING_PENS',
}

export interface ImageEntry {
  id: string;
  picture: HTMLCanvasElement;
}

export interface ActivateLayerAction extends Action {
  type: ActionNames.ACTIVATE_LAYER;
  layerID: number;
}

export interface ReplaceHandwritingTilesAction extends Action {
  type: ActionNames.REPLACE_HANDWRITING_TILES;
  tiles: Tiling<ImageEntry>;
}

export interface AddFigureAction extends Action, DrawingShape, Fillable {
  type: ActionNames.ADD_FIGURE;
  kind: ShapeKindGroupFigure;
}

interface AddNoteAction extends Action, NoteShape, Fillable {
}

export interface AddStickyAction extends AddNoteAction {
  type: ActionNames.ADD_STICKY;
  kind: ShapeKind.Sticky;
}

export interface AddBalloonAction extends AddNoteAction {
  type: ActionNames.ADD_BALLOON;
  kind: ShapeKind.Balloon;
  arrowPoint: Point;
}

export interface AddStraightLineAction extends Action, StraightLineShape {
  type: ActionNames.ADD_STRAIGHT_LINE;
}

export interface AddPolylineAction extends Action, PolylineShape {
  type: ActionNames.ADD_POLYLINE;
}

export interface AddPolygonAction extends Action, PolygonShape {
  type: ActionNames.ADD_POLYGON;
}

export interface AddStampAction extends Action {
  type: ActionNames.ADD_STAMP;
  stampId: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PasteImageAction extends Action {
  type: ActionNames.PASTE_IMAGE;
  x: number;
  y: number;
  alpha: number;
  source: ImageEntry;
}

export interface PasteMultiroleLinkAction extends Action, MultiroleLinkShape {
  type: ActionNames.PASTE_MULTIROLE_LINK;
}

export interface MoveLayerAction extends Action {
  type: ActionNames.MOVE_LAYER;
  dx: number;
  dy: number;
}

export interface ResizeLayerAction extends Action {
  type: ActionNames.RESIZE_LAYER;
  dx: number;
  dy: number;
  dwidth: number;
  dheight: number;
}

export interface RemoveLayerAction extends Action {
  type: ActionNames.REMOVE_LAYER;
  layerID: number;
}

export interface ClearAllObjectsAction extends Action {
  type: ActionNames.CLEAR_ALL_OBJECTS;
}

export interface ClearAllLearningPenRectsAction extends Action {
  type: ActionNames.CLEAR_ALL_LEARNING_PEN_RECTS;
}

export interface ClearAllHandwritingAction extends Action {
  type: ActionNames.CLEAR_ALL_HANDWRITING;
}

export interface RemoveMultiroleLinkAction extends Action {
  type: ActionNames.REMOVE_MULTIROLE_LINK;
  multiroleLinkID: string;
}

export interface ClearEverythingAction extends Action {
  type: ActionNames.CLEAR_EVERYTHING;
}

export interface ChangeTextAction extends Action {
  type: ActionNames.CHANGE_TEXT;
  text: string;
}

export interface StartTextEditingAction extends Action {
  type: ActionNames.START_TEXT_EDITING;
}

export interface MovePointAction extends Action {
  type: ActionNames.MOVE_POINT;
  offset: Offset;
}

export interface FoldNoteAction extends Action {
  type: ActionNames.FOLD_NOTE;
  layerID: number;
  isFolded: boolean;
}

export interface ToggleTransparencyOfLearningPenAction extends Action {
  type: ActionNames.TOGGLE_TRANSPARENCY_OF_LEARNING_PEN;
  layerID: number;
  isTransparency: boolean;
}

export interface ToggleTransparencyOfAllLearningPensAction extends Action {
  type: ActionNames.TOGGLE_TRANSPARENCY_OF_ALL_LEARNING_PENS;
  isTransparency: boolean;
}

export type DrawingAction =
  ActivateLayerAction | ReplaceHandwritingTilesAction | AddFigureAction | AddStickyAction |
  AddBalloonAction | AddStraightLineAction | AddPolylineAction | AddPolygonAction |
  PasteImageAction | PasteMultiroleLinkAction | MoveLayerAction | ResizeLayerAction |
  RemoveLayerAction | ClearAllObjectsAction | ClearAllHandwritingAction |
  RemoveMultiroleLinkAction | ClearEverythingAction | StartTextEditingAction |
  ChangeTextAction | MovePointAction | FoldNoteAction | AddStampAction |
  ToggleTransparencyOfLearningPenAction | ToggleTransparencyOfAllLearningPensAction |
  ClearAllLearningPenRectsAction;

export const activateLayer = (layerID: number): ActivateLayerAction => ({
  layerID,
  type: ActionNames.ACTIVATE_LAYER,
});
export const replaceHandwritingTiles = (
  tiles: Tiling<ImageEntry>,
): ReplaceHandwritingTilesAction => ({
  tiles, type: ActionNames.REPLACE_HANDWRITING_TILES,
});
export const addFigure = (
  kind: ShapeKindGroupFigure,
  x: number, y: number, width: number, height: number,
  fillColor: string, lineColor: string, lineWidth: number, lineType: LineType,
  lineCaps: LineCaps, alpha: number, isFilled: boolean,
): AddFigureAction => ({
  kind, x, y, width, height, fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled,
  type: ActionNames.ADD_FIGURE,
});
export const addSticky = (
  x: number, y: number, width: number, height: number,
  fillColor: string, lineColor: string, lineWidth: number, lineType: LineType,
  lineCaps: LineCaps, alpha: number, isFilled: boolean,
  text: string, textColor: string, fontSize: number, fontType: FontType, isFolded: boolean,
  lineHeight: number, padding: number,
): AddStickyAction => ({
  x, y, width, height, fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled,
  text, textColor, fontSize, fontType, isFolded,
  lineHeight, padding,
  type: ActionNames.ADD_STICKY, kind: ShapeKind.Sticky,
});
export const addBalloon = (
  x: number, y: number, width: number, height: number,
  fillColor: string, lineColor: string, lineWidth: number, lineType: LineType,
  lineCaps: LineCaps, alpha: number, isFilled: boolean,
  text: string, textColor: string, fontSize: number, fontType: FontType, isFolded: boolean,
  lineHeight: number, padding: number,
  arrowPoint: Point,
): AddBalloonAction => ({
  x, y, width, height, fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled,
  text, textColor, fontSize, fontType, isFolded, arrowPoint,
  lineHeight, padding,
  type: ActionNames.ADD_BALLOON, kind: ShapeKind.Balloon,
});
export const addStraightLine = (
  kind: ShapeKind.StraightLine | ShapeKind.AxisAlignedStraightLine,
  line: Line, lineColor: string, lineWidth: number,
  lineType: LineType, lineCaps: LineCaps, alpha: number,
): AddStraightLineAction => ({
  kind, line, lineColor, lineWidth, lineType, lineCaps, alpha, type: ActionNames.ADD_STRAIGHT_LINE,
});
export const addPolyline = (
  lineColor: string, lineWidth: number, lineType: LineType, lineCaps: LineCaps, alpha: number,
  vertices: ReadonlyArray<Point>,
): AddPolylineAction => ({
  lineColor, lineWidth, lineType, lineCaps, alpha, vertices,
  type: ActionNames.ADD_POLYLINE, kind: ShapeKind.Polyline,
});
export const addPolygon = (
  fillColor: string, lineColor: string, lineWidth: number, lineType: LineType, lineCaps: LineCaps,
  alpha: number, isFilled: boolean,
  vertices: ReadonlyArray<Point>,
): AddPolygonAction => ({
  fillColor, lineColor, lineWidth, lineType, lineCaps, alpha, isFilled, vertices,
  type: ActionNames.ADD_POLYGON, kind: ShapeKind.Polygon,
});

export const addStamp = (
  stampId: string,
  x: number,
  y: number,
  width: number,
  height: number,
): AddStampAction => ({
  x, y, width, height,
  stampId,
  type: ActionNames.ADD_STAMP,
});

export const pasteImage = (
  source: ImageEntry, x: number, y: number, alpha: number,
): PasteImageAction => ({
  x, y, alpha, source,
  type: ActionNames.PASTE_IMAGE,
});
export const pasteMultiroleLink = (
  x: number, y: number, multiroleLink: MultiroleLinkInfo,
): PasteMultiroleLinkAction => ({
  x, y, multiroleLink,
  type: ActionNames.PASTE_MULTIROLE_LINK, kind: ShapeKind.MultiroleLink,
});
export const moveLayer = (dx: number, dy: number): MoveLayerAction => ({
  dx, dy,
  type: ActionNames.MOVE_LAYER,
});
export const resizeLayer = (
  dx: number, dy: number, dwidth: number, dheight: number,
): ResizeLayerAction => ({
  dx, dy, dwidth, dheight,
  type: ActionNames.RESIZE_LAYER,
});
export const removeLayer = (layerID: number): RemoveLayerAction => ({
  layerID,
  type: ActionNames.REMOVE_LAYER,
});
export const clearAllObjects = (): ClearAllObjectsAction => ({
  type: ActionNames.CLEAR_ALL_OBJECTS,
});
export const clearAllLearningPenRects = (): ClearAllLearningPenRectsAction => ({
  type: ActionNames.CLEAR_ALL_LEARNING_PEN_RECTS,
});
export const clearAllHandwriting = (): ClearAllHandwritingAction => ({
  type: ActionNames.CLEAR_ALL_HANDWRITING,
});
export const removeMultiroleLink = (multiroleLinkID: string): RemoveMultiroleLinkAction => ({
  multiroleLinkID,
  type: ActionNames.REMOVE_MULTIROLE_LINK,
});
export const clearEverything = (): ClearEverythingAction => ({
  type: ActionNames.CLEAR_EVERYTHING,
});
export const changeText = (text: string): ChangeTextAction => ({
  text, type: ActionNames.CHANGE_TEXT,
});
export const startTextEditing = (): StartTextEditingAction => ({
  type: ActionNames.START_TEXT_EDITING,
});
export const movePoint = (offset: Offset): MovePointAction => ({
  offset, type: ActionNames.MOVE_POINT,
});
export const foldNote = (layerID: number, isFolded: boolean): FoldNoteAction => ({
  layerID, isFolded, type: ActionNames.FOLD_NOTE,
});
export const toggleTransparencyOfLearningPen = (layerID: number, isTransparency: boolean):
  ToggleTransparencyOfLearningPenAction => ({
    layerID, isTransparency, type: ActionNames.TOGGLE_TRANSPARENCY_OF_LEARNING_PEN,
  });

export const toggleTransparencyOfAllLearningPens = (isTransparency: boolean):
  ToggleTransparencyOfAllLearningPensAction => ({
    isTransparency,
    type: ActionNames.TOGGLE_TRANSPARENCY_OF_ALL_LEARNING_PENS,
  });

export const isDrawingAction = (action: Action): action is DrawingAction => {
  return Object.keys(ActionNames).some(k => action.type === ActionNames[k as any]);
};

export const isEditingDrawingAction = (action: DrawingAction): boolean => {
  switch (action.type) {
    case ActionNames.ACTIVATE_LAYER:
    case ActionNames.CHANGE_TEXT:
    case ActionNames.FOLD_NOTE:
    case ActionNames.TOGGLE_TRANSPARENCY_OF_LEARNING_PEN:
    case ActionNames.TOGGLE_TRANSPARENCY_OF_ALL_LEARNING_PENS:
      return false;

    case ActionNames.REPLACE_HANDWRITING_TILES:
    case ActionNames.ADD_FIGURE:
    case ActionNames.ADD_STICKY:
    case ActionNames.ADD_BALLOON:
    case ActionNames.ADD_STRAIGHT_LINE:
    case ActionNames.ADD_POLYLINE:
    case ActionNames.ADD_POLYGON:
    case ActionNames.PASTE_IMAGE:
    case ActionNames.PASTE_MULTIROLE_LINK:
    case ActionNames.MOVE_LAYER:
    case ActionNames.RESIZE_LAYER:
    case ActionNames.REMOVE_LAYER:
    case ActionNames.CLEAR_ALL_OBJECTS:
    case ActionNames.CLEAR_ALL_LEARNING_PEN_RECTS:
    case ActionNames.CLEAR_ALL_HANDWRITING:
    case ActionNames.REMOVE_MULTIROLE_LINK:
    case ActionNames.CLEAR_EVERYTHING:
    case ActionNames.START_TEXT_EDITING:
    case ActionNames.MOVE_POINT:
    case ActionNames.ADD_STAMP:

      return true;
  }
};

export const isSingleAddingAction = (action: DrawingAction): boolean => {
  switch (action.type) {
    case ActionNames.ACTIVATE_LAYER:
    case ActionNames.CHANGE_TEXT:
    case ActionNames.MOVE_LAYER:
    case ActionNames.RESIZE_LAYER:
    case ActionNames.REMOVE_LAYER:
    case ActionNames.CLEAR_ALL_OBJECTS:
    case ActionNames.CLEAR_ALL_LEARNING_PEN_RECTS:
    case ActionNames.CLEAR_ALL_HANDWRITING:
    case ActionNames.REMOVE_MULTIROLE_LINK:
    case ActionNames.CLEAR_EVERYTHING:
    case ActionNames.START_TEXT_EDITING:
    case ActionNames.MOVE_POINT:
    case ActionNames.FOLD_NOTE:
    case ActionNames.REPLACE_HANDWRITING_TILES:
    case ActionNames.ADD_FIGURE:
    case ActionNames.ADD_STRAIGHT_LINE:
    case ActionNames.ADD_POLYLINE:
    case ActionNames.ADD_POLYGON:
    case ActionNames.PASTE_IMAGE:
    case ActionNames.ADD_STAMP:
    case ActionNames.TOGGLE_TRANSPARENCY_OF_LEARNING_PEN:
    case ActionNames.TOGGLE_TRANSPARENCY_OF_ALL_LEARNING_PENS:
      return false;

    case ActionNames.PASTE_MULTIROLE_LINK:
    case ActionNames.ADD_STICKY:
    case ActionNames.ADD_BALLOON:
      return true;
  }
};
